import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Welcome to Peak. Your workspace is ready.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="register-page">
      <div className="bg-[var(--peak-blue-deep)] text-white p-10 md:p-16 flex flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-black flex items-center justify-center font-heading font-black text-xl">P</div>
          <div className="font-heading font-black text-2xl tracking-tighter">PEAK CRM</div>
        </div>
        <div>
          <h1 className="font-heading font-black uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter">
            Build your<br/>revenue engine.<br/>In minutes.
          </h1>
          <p className="mt-6 text-lg font-medium max-w-md">
            Get your account. Start piping leads, booking calls and launching funnels today.
          </p>
        </div>
        <div className="section-label text-white/80">© 2026 Peak Labs.</div>
      </div>

      <div className="bg-white p-10 md:p-16 flex items-center">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <div>
            <div className="section-label">Get started · free</div>
            <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-1">Create your workspace.</h2>
            <p className="text-sm text-[var(--peak-muted)] mt-2">
              No credit card. 60-second setup. A welcome contact and starter pipeline
              are waiting inside.
            </p>
          </div>
          <div>
            <label className="section-label" htmlFor="name">Full name</label>
            <input id="name" data-testid="register-name" className="brutalist-input" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label className="section-label" htmlFor="email">Email</label>
            <input id="email" data-testid="register-email" className="brutalist-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <label className="section-label" htmlFor="password">Password (min 6)</label>
            <input id="password" data-testid="register-password" className="brutalist-input" type="password" value={password} minLength={6} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button type="submit" data-testid="register-submit" disabled={loading} className="w-full bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-4 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {loading ? "Creating…" : "Create workspace"} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="text-sm">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-login" className="font-bold underline hover:text-[var(--peak-accent)]">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
